/**
 * The TensorLens mark, as inline SVG.
 *
 * A lens ring with a weight distribution inside it: a bell of four bars plus one
 * isolated tick far out in the tail. The outlier is the whole point of the
 * product — heavy tails are what break naive quantization — so it is the one
 * detail that has to survive down to favicon size.
 *
 * Inline rather than an `<img src="logo.svg">` so the mark inherits the page's
 * theme through `currentColor` and `var(--accent)`; an external SVG document
 * cannot see the page's custom properties.
 *
 * **Three copies of this geometry exist, and all three must change together.**
 * Each standalone context is standalone for a different reason, and none of
 * them can read the page's custom properties:
 *
 * - `site/public/favicon.svg` — a separate document, accent hexes inlined, with
 *   its own `prefers-color-scheme` block.
 * - `lensWatermark` in `site/src/render/card-map.ts` — the share-card watermark.
 *   Takes its colour resolved, and reproduces the clip arithmetically because a
 *   `clipPath` needs an id, and an id is both non-deterministic (random, here)
 *   and a collision waiting to happen in an embedded card.
 * - this one.
 *
 * The mark is decorative wherever the accessible name comes from adjacent text
 * (the header pairs it with the wordmark), so callers pass `title: undefined`
 * there and let `aria-hidden` do its job.
 */
export interface LogoOptions {
  /**
   * Accessible name. Omit when neighbouring text already names the mark — the
   * SVG is then marked `aria-hidden` so screen readers don't hear it twice.
   */
  readonly title?: string;
  readonly className?: string;
}

export function logoSvg({ title, className = "brand-mark" }: LogoOptions = {}): string {
  // Unique per call: two inline SVGs on one page sharing a clipPath id would
  // make the second one silently reuse the first one's clip.
  const clipId = `tl-lens-${Math.random().toString(36).slice(2, 9)}`;
  const labelled = title
    ? `role="img" aria-label="${title}"`
    : `role="presentation" aria-hidden="true"`;

  return `<svg class="${className}" viewBox="0 0 32 32" ${labelled}>
  <defs><clipPath id="${clipId}"><circle cx="16" cy="16" r="12.4"/></clipPath></defs>
  <g clip-path="url(#${clipId})" fill="var(--accent)">
    <rect x="7.5" y="16.5" width="3" height="7.4" rx="1"/>
    <rect x="11.5" y="10.5" width="3" height="13.4" rx="1"/>
    <rect x="15.5" y="8" width="3" height="15.9" rx="1"/>
    <rect x="19.5" y="14" width="3" height="9.9" rx="1"/>
    <rect x="23" y="20.2" width="2.2" height="3.7" rx="1"/>
    <rect x="5" y="22.6" width="22" height="1.3"/>
  </g>
  <circle cx="16" cy="16" r="12.4" fill="none" stroke="var(--accent)" stroke-width="2.2"/>
</svg>`;
}
